"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ShieldCheck, Clock, Wrench, CircleCheck as CheckCircle2, ArrowRight, Droplets } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Repiping() {
  const shouldReduceMotion = useReducedMotion();
  const keyBenefits = [{
    text: "Whole-home copper & PEX solutions"
  }, {
    text: "24/7 fast emergency response"
  }, {
    text: "Licensed & insured local specialists"
  }, {
    text: "Clear pricing with no surprises"
  }];
  return <section id="repipinghero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 lg:py-28 overflow-hidden border-b border-border">
      {/* Background subtle geometric tint */}
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(ellipse_at_top_right,var(--color-primary)/0.08,transparent_60%)]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Content */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6 text-left">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-semibold text-xs text-secondary-foreground flex items-center gap-1.5">
                <ShieldCheck className="size-3.5 text-primary" />
                Jonesboro Plumbing Experts
              </Badge>
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Clock className="size-3 text-muted-foreground" />
                Available 24 Hours / 7 Days
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15]">
              Reliable Home <span className="text-primary">Repiping Services</span> in Jonesboro
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Stop recurring leaks, low water pressure, and discolored water. We replace damaged, aging galvanized or polybutylene pipes with durable PEX and copper systems installed with minimal disruption to your home.
            </p>

            {/* Quick Benefits Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {keyBenefits.map((benefit, i) => <div key={benefit.text} data-index={i} className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{benefit.text}</span>
                </div>)}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <Button asChild size="lg" className="h-12 px-6 font-semibold shadow-md gap-2">
                <a href="tel:4708479076">
                  <PhoneCall className="size-5" />
                  Call Now: (470) 847-9076
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 px-6 font-semibold">
                <Link to="/contact/">
                  <span>Schedule an Inspection</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>

            {/* Trust highlights */}
            <div className="pt-6 border-t border-border flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Wrench className="size-4 text-primary" />
                <span>Full & Partial Home Repipes</span>
              </div>
              <div className="flex items-center gap-2">
                <Droplets className="size-4 text-primary" />
                <span>Clean Water & Strong Pressure</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Visual Graphic / Hero Image */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Main Image Container */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_working_on_home_pipes.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_working_on_home_pipes.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_working_on_home_pipes.png-wvc-sizes" alt="Plumbing professional installing clean new pipes in a home" className="w-full h-[360px] sm:h-[420px] object-cover" loading="lazy" decoding="async" />

                {/* Floating Info Card */}
                <div className="absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur-sm border border-border rounded-xl p-4 shadow-lg">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                        24/7
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-card-foreground">Emergency Dispatch</p>
                        <p className="text-xs text-muted-foreground">Prompt arrival across Jonesboro, GA</p>
                      </div>
                    </div>
                    <Button asChild size="sm" variant="secondary" className="h-8 text-xs font-semibold shrink-0">
                      <Link to="/services/">All Services</Link>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Decorative background blur badge */}
              <div className="absolute -top-4 -right-4 -z-10 size-32 bg-primary/15 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-4 -left-4 -z-10 size-32 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}